import React, { useState, useRef, useEffect } from "react";
import {
    PrimaryButton,
    DefaultButton,
    Stack,
    TextField,
    Spinner,
    Label,
    MessageBar,
    MessageBarType,
    Icon,
} from "@fluentui/react";
import { createDecorationStyleFromImage, StyleFromImageResult } from "src/services/aigc/workflowOnline";
import style from "./index.module.scss";

// Predefined color options for easy selection
const colorOptions = [
    { hex: "#C41E3A", name: "Crimson Red" },
    { hex: "#1E3A5F", name: "Navy Blue" },
    { hex: "#2E7D32", name: "Forest Green" },
    { hex: "#E65100", name: "Deep Orange" },
    { hex: "#6A1B9A", name: "Purple" },
    { hex: "#F9A825", name: "Amber Yellow" },
    { hex: "#00695C", name: "Teal" },
    { hex: "#5D4037", name: "Brown" },
    { hex: "#C2185B", name: "Pink" },
    { hex: "#0078D4", name: "Microsoft Blue" },
];

// Result state for blob-based response
interface ResultState {
    blob: Blob;
    blobUrl: string;
    extractedSubject: string | null;
    mainColor: string | null;
    styleSummary: string | null;
}

export const IconCreateWithImage: React.FC = () => {
    const [content, setContent] = useState("");
    const [mainColor, setMainColor] = useState("#C41E3A");
    const [customColor, setCustomColor] = useState("");
    const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
    const [backgroundImagePreview, setBackgroundImagePreview] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ResultState | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Clean up blob URL when component unmounts
    useEffect(() => () => {
        if (result?.blobUrl) {
            URL.revokeObjectURL(result.blobUrl);
        }
    }, [result]);

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith("image/")) {
            setError("Please upload an image file (PNG, JPG, etc.)");
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setError("Image file is too large. Maximum size is 10MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            // Extract base64 data (remove data:image/xxx;base64, prefix)
            const base64Data = dataUrl.split(",")[1];
            setBackgroundImage(base64Data);
            setBackgroundImagePreview(dataUrl);
            setError(null);
        };
        reader.onerror = () => {
            setError("Failed to read image file.");
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setBackgroundImage(null);
        setBackgroundImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleCreate = async () => {
        if (!content.trim()) {
            setError("Please enter a content description.");
            return;
        }

        if (!backgroundImage) {
            setError("Please upload a reference image for style transfer.");
            return;
        }

        const colorToUse = customColor.match(/^#[0-9A-Fa-f]{6}$/)
            ? customColor
            : mainColor;

        setError(null);
        setIsLoading(true);

        // Clean up previous blob URL
        if (result?.blobUrl) {
            URL.revokeObjectURL(result.blobUrl);
        }
        setResult(null);

        try {
            const response: StyleFromImageResult = await createDecorationStyleFromImage({
                content: content.trim(),
                mainColor: colorToUse,
                backgroundImage,
            });

            // Create blob URL for preview
            const blobUrl = URL.createObjectURL(response.blob);
            setResult({
                blob: response.blob,
                blobUrl,
                extractedSubject: response.extractedSubject,
                mainColor: response.mainColor,
                styleSummary: response.styleSummary,
            });
        } catch (err: unknown) {
            const axiosError = err as {
                response?: { data?: { message?: string } };
                message?: string;
            };
            const message = axiosError?.response?.data?.message
                || axiosError?.message
                || String(err);
            setError(`Failed to create icon: ${message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setContent("");
        setMainColor("#C41E3A");
        setCustomColor("");
        setBackgroundImage(null);
        setBackgroundImagePreview(null);
        // Clean up blob URL before clearing
        if (result?.blobUrl) {
            URL.revokeObjectURL(result.blobUrl);
        }
        setResult(null);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDownload = () => {
        if (!result?.blob) return;

        // Use the blob directly - no conversion needed
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `icon-styled-${result.extractedSubject?.replace(/\s+/g, "_") || "decoration"}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const isValidCustomColor = customColor.match(/^#[0-9A-Fa-f]{6}$/);
    const selectedColor = isValidCustomColor ? customColor : mainColor;

    return (
        <div className={style.iconCreatePage}>
            <h1>Icon Create with Style Transfer</h1>
            <p className={style.description}>
                Generate icons that match the visual style of a reference image.
                Upload a reference image, and the AI will extract its style and apply it to the generated icon.
            </p>

            <Stack tokens={{ childrenGap: 16 }} className={style.container}>
                {/* Reference Image Upload */}
                <div className={style.inputSection}>
                    <Label required>Reference Image (for style extraction)</Label>
                    <p className={style.helperText}>
                        Upload an image whose visual style you want the icon to match.
                        The AI will analyze this image&apos;s colors, textures, rendering style, and apply it to the generated icon.
                    </p>
                    <div className={style.imageUploadArea}>
                        {backgroundImagePreview ? (
                            <div className={style.imagePreviewContainer}>
                                <img
                                    src={backgroundImagePreview}
                                    alt="Reference"
                                    className={style.imagePreview}
                                />
                                <DefaultButton
                                    className={style.removeImageBtn}
                                    onClick={handleRemoveImage}
                                    iconProps={{ iconName: "Delete" }}
                                    text="Remove"
                                />
                            </div>
                        ) : (
                            <div
                                className={style.uploadPlaceholder}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Icon iconName="CloudUpload" className={style.uploadIcon} />
                                <span>Click to upload reference image</span>
                                <span className={style.uploadHint}>PNG, JPG, max 10MB</span>
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className={style.hiddenInput}
                        />
                    </div>
                </div>

                {/* Content Input */}
                <div className={style.inputSection}>
                    <Label required>Content Description</Label>
                    <TextField
                        multiline
                        rows={4}
                        placeholder="Enter a description of the icon content you want to visualize (e.g., 'heart', 'neural network', 'leaf')"
                        value={content}
                        onChange={(_, v) => setContent(v || "")}
                        styles={{ root: { width: "100%" } }}
                    />
                </div>

                {/* Color Selection */}
                <div className={style.inputSection}>
                    <Label required>Main Color</Label>
                    <div className={style.colorGrid}>
                        {colorOptions.map((color) => (
                            <div
                                key={color.hex}
                                className={`${style.colorOption} ${
                                    mainColor === color.hex && !customColor
                                        ? style.colorSelected
                                        : ""
                                }`}
                                style={{ backgroundColor: color.hex }}
                                onClick={() => {
                                    setMainColor(color.hex);
                                    setCustomColor("");
                                }}
                                title={color.name}
                            />
                        ))}
                    </div>
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 12 }}
                        verticalAlign="end"
                        className={style.customColorRow}
                    >
                        <TextField
                            label="Custom Color (Hex)"
                            placeholder="#RRGGBB"
                            value={customColor}
                            onChange={(_, v) => setCustomColor(v || "")}
                            styles={{ root: { width: 150 } }}
                        />
                        {customColor && (
                            <div
                                className={style.customColorPreview}
                                style={{
                                    backgroundColor: isValidCustomColor
                                        ? customColor
                                        : "#ccc",
                                }}
                            />
                        )}
                    </Stack>
                    <div className={style.selectedColorInfo}>
                        {`Selected: ${selectedColor}`}
                    </div>
                </div>

                {/* Action Buttons */}
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                    <PrimaryButton
                        text="Create Icon with Style"
                        onClick={handleCreate}
                        disabled={isLoading || !content.trim() || !backgroundImage}
                    />
                    <DefaultButton
                        text="Clear All"
                        onClick={handleClear}
                        disabled={isLoading}
                    />
                </Stack>

                {/* Loading State */}
                {isLoading && (
                    <div className={style.loadingSection}>
                        <Spinner label="Analyzing style and generating icon... This may take up to 60 seconds." />
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                    >
                        {error}
                    </MessageBar>
                )}

                {/* Result Display */}
                {result && result.blobUrl && (
                    <div className={style.resultSection}>
                        <Label>Generated Icon</Label>

                        {/* Style Info */}
                        {result.styleSummary && (
                            <div className={style.styleInfo}>
                                <strong>Extracted Style:</strong>
                                {" "}
                                {result.styleSummary}
                            </div>
                        )}

                        <div className={style.svgPreview}>
                            <img
                                className={style.svgImage}
                                src={result.blobUrl}
                                alt="Generated Icon"
                            />
                        </div>

                        {result.extractedSubject && (
                            <div className={style.subjectInfo}>
                                <strong>Subject:</strong>
                                {" "}
                                {result.extractedSubject}
                            </div>
                        )}

                        {result.mainColor && (
                            <div className={style.colorInfo}>
                                <strong>Main Color:</strong>
                                {" "}
                                {result.mainColor}
                                <span
                                    className={style.colorSwatch}
                                    style={{ backgroundColor: result.mainColor }}
                                />
                            </div>
                        )}

                        <Stack
                            horizontal
                            tokens={{ childrenGap: 12 }}
                            className={style.resultActions}
                        >
                            <PrimaryButton
                                text="Download PNG"
                                onClick={handleDownload}
                                iconProps={{ iconName: "Download" }}
                            />
                        </Stack>
                    </div>
                )}
            </Stack>
        </div>
    );
};

export default IconCreateWithImage;
